"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Copy, Check, Trash2, ArrowDown } from "lucide-react"
import { toast } from "sonner"

interface BaseValue {
  label: string
  base: number
  value: string
  placeholder: string
  prefix?: string
}

const initialBases: BaseValue[] = [
  { label: "Decimal", base: 10, value: "", placeholder: "42", prefix: "" },
  { label: "Binary", base: 2, value: "", placeholder: "101010", prefix: "0b" },
  { label: "Octal", base: 8, value: "", placeholder: "52", prefix: "0o" },
  { label: "Hexadecimal", base: 16, value: "", placeholder: "2A", prefix: "0x" },
]

function isValidForBase(value: string, base: number): boolean {
  if (!value) return true
  const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ".slice(0, base)
  const regex = new RegExp(`^[${chars}]+$`, "i")
  return regex.test(value)
}

function convertFromBase(value: string, fromBase: number): number | null {
  if (!value.trim()) return null
  try {
    // Handle negative numbers
    const isNegative = value.startsWith("-")
    const absValue = isNegative ? value.slice(1) : value
    const result = parseInt(absValue, fromBase)
    if (isNaN(result)) return null
    return isNegative ? -result : result
  } catch {
    return null
  }
}

function convertToBase(value: number, toBase: number): string {
  const isNegative = value < 0
  const absValue = isNegative ? -value : value
  const result = absValue.toString(toBase).toUpperCase()
  return isNegative ? `-${result}` : result
}

export function NumberBaseConverter() {
  const [bases, setBases] = useState<BaseValue[]>(initialBases)
  const [activeBase, setActiveBase] = useState<number | null>(null)
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const updateAllBases = useCallback((value: string, fromBase: number) => {
    setError(null)

    if (!value.trim()) {
      setBases(initialBases)
      return
    }

    if (!isValidForBase(value, fromBase)) {
      setError(`Invalid character for base ${fromBase}`)
      return
    }

    const decimalValue = convertFromBase(value, fromBase)
    if (decimalValue === null) {
      setError("Invalid number")
      return
    }

    setBases((prev) =>
      prev.map((b) => ({
        ...b,
        value: b.base === fromBase ? value : convertToBase(decimalValue, b.base),
      }))
    )
  }, [])

  const handleChange = (index: number, value: string) => {
    const base = bases[index]
    // Remove any prefix if user pastes with it
    let cleanValue = value.toUpperCase()
    if (base.prefix && cleanValue.startsWith(base.prefix.toUpperCase())) {
      cleanValue = cleanValue.slice(base.prefix.length)
    }

    setActiveBase(base.base)
    updateAllBases(cleanValue, base.base)
  }

  const copyValue = async (index: number) => {
    const value = bases[index].value
    if (!value) return

    await navigator.clipboard.writeText(value)
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
    toast.success("Copied to clipboard")
  }

  const copyWithPrefix = async (index: number) => {
    const base = bases[index]
    if (!base.value) return

    const valueWithPrefix = base.prefix ? `${base.prefix}${base.value}` : base.value
    await navigator.clipboard.writeText(valueWithPrefix)
    setCopiedIndex(index)
    setTimeout(() => setCopiedIndex(null), 2000)
    toast.success("Copied with prefix")
  }

  const clear = () => {
    setBases(initialBases)
    setActiveBase(null)
    setError(null)
  }

  const loadExample = () => {
    updateAllBases("255", 10)
    setActiveBase(10)
  }

  // Common values reference
  const commonValues = [
    { dec: "0", bin: "0", hex: "0" },
    { dec: "1", bin: "1", hex: "1" },
    { dec: "255", bin: "11111111", hex: "FF" },
    { dec: "256", bin: "100000000", hex: "100" },
    { dec: "1024", bin: "10000000000", hex: "400" },
    { dec: "65535", bin: "1111111111111111", hex: "FFFF" },
  ]

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Number Base Converter</CardTitle>
          <CardDescription>
            Convert numbers between decimal, binary, octal, and hexadecimal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Actions */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadExample}>
              Load Example (255)
            </Button>
            <Button variant="outline" size="sm" onClick={clear}>
              <Trash2 className="h-4 w-4 mr-1" />
              Clear
            </Button>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Base Inputs */}
          <div className="space-y-4">
            {bases.map((base, index) => (
              <div key={base.label} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`base-${base.base}`} className="flex items-center gap-2">
                    {base.label}
                    <span className="text-xs text-muted-foreground">(base {base.base})</span>
                  </Label>
                  <div className="flex gap-1">
                    {base.prefix && base.value && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyWithPrefix(index)}
                        className="h-7 text-xs"
                      >
                        Copy {base.prefix}...
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => copyValue(index)}
                      disabled={!base.value}
                    >
                      {copiedIndex === index ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2">
                  {base.prefix && (
                    <span className="flex items-center px-3 rounded-md bg-muted text-muted-foreground text-sm font-mono">
                      {base.prefix}
                    </span>
                  )}
                  <Input
                    id={`base-${base.base}`}
                    type="text"
                    placeholder={base.placeholder}
                    value={base.value}
                    onChange={(e) => handleChange(index, e.target.value)}
                    className="font-mono"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Conversion indicator */}
          {activeBase !== null && bases[0].value && (
            <div className="flex items-center justify-center text-muted-foreground text-sm gap-2 py-2">
              <ArrowDown className="h-4 w-4" />
              <span>All values update in real-time</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4 font-medium">Decimal</th>
                  <th className="text-left py-2 pr-4 font-medium">Binary</th>
                  <th className="text-left py-2 font-medium">Hexadecimal</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {commonValues.map((row) => (
                  <tr key={row.dec} className="border-b border-muted">
                    <td className="py-2 pr-4">{row.dec}</td>
                    <td className="py-2 pr-4">{row.bin}</td>
                    <td className="py-2">{row.hex}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Conversion Tips</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Binary (base 2):</strong> Uses only 0 and 1. Common prefix: 0b</li>
            <li><strong>Octal (base 8):</strong> Uses digits 0-7. Common prefix: 0o</li>
            <li><strong>Decimal (base 10):</strong> Standard number system (0-9)</li>
            <li><strong>Hexadecimal (base 16):</strong> Uses 0-9 and A-F. Common prefix: 0x</li>
          </ul>
          <p className="mt-4">
            <strong>Tip:</strong> Type in any field and all other bases update automatically.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
